import { useGSAP } from '@gsap/react';

import { graphql } from 'gatsby';
import gsap from 'gsap';
import React, { useRef } from 'react';
import { SanityImageType } from '../../../graphql-fragments/SanityImage';
import { ModuleBgColor } from '../../../templates/Page';
import { clsx } from '../../../utils/utils';
import { CommonModuleProps } from '../../ModulesContent';
import Image from '../Image';
import ModuleLayout from '../ModuleLayout';
import * as styles from './CircularTextGridModule.module.scss';
import DiagonalLine from './DiagonalLine';

export const CircularTextGridModuleFragment = graphql`
  fragment CircularTextGridModule on SanityPageCircularTextGridModule {
    title {
      ...LocaleStringFragment
    }
    mainImage {
      ...SanityImage
    }
    textItems {
      title {
        ...LocaleStringFragment
      }
      text {
        ...LocaleTextFragment
      }
      icon {
        ...SanityImage
      }
    }
  }
`;

interface TextItem {
  title: string;
  text: string;
  icon?: SanityImageType;
}

export type CircularTextGridModuleProps = {
  title: string;
  mainImage: SanityImageType;
  textItems: Array<TextItem>;

  className?: string;
};

export function getModuleBgColor(props: CircularTextGridModuleProps): ModuleBgColor {
  /**
   * The purpose of this function is to let other modules know which background color this module has.
   * Knowing this, we can use this function to make decisions about the layout and spacing between modules.
   */
  return 'white';
}

function CircularTextGridModule(
  props: CircularTextGridModuleProps & CommonModuleProps,
): React.ReactElement {
  const { title, mainImage, textItems, className, previousModuleBgColor, moduleId } = props;

  const containerRef = useRef<HTMLElement | null>(null);
  useGSAP(
    () => {
      const mobileTextItems = gsap.utils.toArray<HTMLDivElement>(
        `.${styles.mobileContentContainer} .${styles.textContainer}`,
      );

      const desktopTextItems = gsap.utils.toArray<HTMLDivElement>(
        `.${styles.desktopContentContainer} .${styles.textContainer}`,
      );

      const desktopDividers = gsap.utils.toArray<HTMLDivElement>(
        `.${styles.desktopContentContainer} .${styles.dividerLine}`,
      );

      const desktopOrderMap = {
        3: [0, 1, 2],
        4: [0, 2, 3, 1],
        5: [0, 1, 3, 4, 2],
        6: [0, 1, 3, 5, 4, 2],
      };

      const currentOrderMap = desktopOrderMap[desktopTextItems.length];
      const desktopOrderedItems = currentOrderMap.map(index => desktopTextItems[index]);
      const desktopOrderedDividers = currentOrderMap.map(index => desktopDividers[index]);

      [mobileTextItems, desktopOrderedItems].forEach(textItems => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: `.${styles.contentContainer}`,
            start: 'top 60%',
          } satisfies ScrollTrigger.Vars,
        });

        tl.from(textItems, {
          opacity: 0,
          scale: 0.8,
          stagger: 0.3,
          duration: 0.6,
        });
      });

      const tlDividers = gsap.timeline({
        scrollTrigger: {
          trigger: `.${styles.contentContainer}`,
          start: 'top 60%',
        } satisfies ScrollTrigger.Vars,
      });

      tlDividers.from(desktopOrderedDividers, {
        opacity: 0,
        stagger: 0.3,
        duration: 0.6,
      });
    },
    { scope: containerRef },
  );

  function renderTextItemsContainer(textItems: Array<TextItem>, resolution: 'mobile' | 'desktop') {
    return textItems.map((textItem, i) => (
      <React.Fragment key={i}>
        <div
          className={clsx(
            styles.textItemContainer,
            styles[`item${i + 1}`],
            !textItem.icon && styles.noIcon,
            i === textItems.length - 1 && styles.lastItem,
          )}
          key={i}
        >
          {resolution === 'mobile' && <div className={styles.verticalLine}></div>}

          {resolution === 'mobile' && (
            <div className={styles.lineAndDotContainer}>
              <div className={styles.horizontalLine}></div>
              <div className={styles.dotContainer}>
                <div className={styles.dot}></div>
              </div>
            </div>
          )}
          <div className={styles.textContainer}>
            <h4 className={styles.textItemTitle}>{textItem.title}</h4>
            <p className={styles.textItemText}>{textItem.text}</p>
            {textItem.icon && (
              <div className={styles.textItemIconContainer}>
                <Image image={textItem.icon} className={styles.icon} />
              </div>
            )}
          </div>
        </div>
        {resolution === 'desktop' && (
          <div className={clsx(styles.divider, styles[`divider${i + 1}`])}>
            <DiagonalLine className={styles.dividerLine} />
          </div>
        )}
      </React.Fragment>
    ));
  }

  return (
    <ModuleLayout
      id={moduleId}
      moduleRef={containerRef}
      className={clsx(className, styles.container)}
      currentModuleBgColor={getModuleBgColor(props)}
      previousModuleBgColor={previousModuleBgColor}
      contentClassName={styles.contentContainer}
      title={title}
    >
      {/* MOBILE */}
      <div className={clsx(styles.mobileContentContainer)}>
        <div className={styles.contentWrapper}>
          <div className={styles.imageContainer}>
            <div className={styles.verticalLine}></div>
            <div className={styles.horizontalLine}></div>
            <div className={styles.imageWrapper}>
              <Image image={mainImage} className={styles.mainImage} />
            </div>
          </div>
          <div className={styles.textGridContainer}>
            {renderTextItemsContainer(textItems, 'mobile')}
          </div>
        </div>
      </div>
      {/* DESKTOP */}
      <div
        className={clsx(
          styles.desktopContentContainer,
          textItems.length >= 3 && styles[`with${textItems.length}Items`],
        )}
      >
        {renderTextItemsContainer(textItems, 'desktop')}
        <div className={styles.imageContainer}>
          <Image image={mainImage} className={styles.mainImage} />
        </div>
      </div>
    </ModuleLayout>
  );
}

export default CircularTextGridModule;
